import { Controller } from "stimulus";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";

// The pinned-tips carousel. Owns the session's tips (latest-first, index 0 = newest) and shows one
// card at a time. Driven by tip events the chat-panel controller forwards (no subscription of its
// own), deduped by tip id so a broadcast+backfilled tip never renders twice.
export default class PinnedTips extends Controller {
  static targets = ["root", "avatar", "avatarFallback", "name", "amount", "body", "expandToggle", "controls", "indicator", "prevButton", "nextButton"];
  static values = { moderator: Boolean };

  connect() {
    this.tips = []; // latest-first; index 0 is the newest
    this.seen = new Set(); // tip ids already held (dedupe)
    this.index = 0;
    this.expanded = false;
  }

  // Backfill on (re)subscribe: the authoritative latest-first set for the session. Rebuild from it
  // (deduping) so a `removed` missed while disconnected is reconciled, and keep the viewed card if
  // it survived, else reset to the newest.
  populate(event) {
    const incoming = event.detail || [];
    const viewedId = this.tips[this.index] ? String(this.tips[this.index].id) : null;

    this.tips = [];
    this.seen = new Set();
    incoming.forEach((tip) => this.record(tip, { prepend: false }));

    const pos = viewedId ? this.tips.findIndex((tip) => String(tip.id) === viewedId) : -1;
    this.index = pos >= 0 ? pos : 0;
    this.render();
  }

  // `created` broadcast: the newest tip. Prepend and show it.
  addTip(event) {
    if (!this.record(event.detail, { prepend: true })) return; // duplicate
    this.index = 0;
    this.render();
  }

  // `removed` broadcast (moderator hide): drop by id and fix the index / visibility.
  removeTip(event) {
    const id = event.detail && event.detail.id;
    if (id == null) return;

    const pos = this.tips.findIndex((tip) => String(tip.id) === String(id));
    if (pos === -1) return;

    this.tips.splice(pos, 1);
    this.seen.delete(String(id));
    // Removing a tip before the current one shifts everything down, so step the index back to
    // keep the same card visible; then clamp to the new range.
    if (pos < this.index) this.index -= 1;
    if (this.index >= this.tips.length) this.index = Math.max(0, this.tips.length - 1);
    this.render();
  }

  // Add a tip to the list unless its id is already held. Returns true when it was added.
  record(tip, { prepend }) {
    if (!tip || tip.id == null) return false;

    const key = String(tip.id);
    if (this.seen.has(key)) return false;

    this.seen.add(key);
    if (prepend) this.tips.unshift(tip);
    else this.tips.push(tip);
    return true;
  }

  next() {
    if (this.index >= this.tips.length - 1) return;
    this.index += 1;
    this.render();
  }

  prev() {
    if (this.index <= 0) return;
    this.index -= 1;
    this.render();
  }

  // Moderator-only: ask the chat-panel controller (which owns the subscription) to remove the shown
  // tip via a window event. We don't drop it locally - the authoritative `removed` broadcast does.
  remove(event) {
    if (!this.moderatorValue) return;

    const tip = this.tips[this.index];
    if (!tip) return;

    this.dispatch("remove", { target: window, detail: { id: tip.id, tip, trigger: event.currentTarget } });
  }

  toggleExpand() {
    this.expanded = !this.expanded;
    this.bodyTarget.classList.toggle("line-clamp-2", !this.expanded);
    this.expandToggleTarget.textContent = this.expanded ? "Show less" : "Show more";
  }

  render() {
    if (this.tips.length === 0) {
      this.rootTarget.classList.add("hidden");
      return;
    }
    this.rootTarget.classList.remove("hidden");

    const tip = this.tips[this.index];

    if (this.hasAvatarTarget) {
      applyAvatar(this.avatarTarget, this.hasAvatarFallbackTarget ? this.avatarFallbackTarget : null, tip.user);
    }

    this.nameTarget.textContent = (tip.user && tip.user.name) || "";
    this.amountTarget.textContent = formatMoney(tip.amount_cents, tip.currency);

    const body = (tip.body || "").trim();
    this.bodyTarget.textContent = body;
    this.bodyTarget.classList.toggle("hidden", body === "");

    // Reset the clamp on each card change, then re-measure for the expand toggle.
    this.expanded = false;
    this.bodyTarget.classList.add("line-clamp-2");
    this.expandToggleTarget.textContent = "Show more";
    this.refreshExpandToggle();

    const many = this.tips.length > 1;
    this.controlsTarget.classList.toggle("hidden", !many);
    this.controlsTarget.classList.toggle("flex", many);
    this.indicatorTarget.textContent = `${this.index + 1} / ${this.tips.length}`;
    this.prevButtonTarget.disabled = this.index === 0;
    this.nextButtonTarget.disabled = this.index >= this.tips.length - 1;
  }

  // Show the expand toggle only when the clamped body overflows two lines (and there is a
  // body at all). Measured on the next frame so layout is settled after the text swap.
  refreshExpandToggle() {
    window.requestAnimationFrame(() => {
      if (!this.hasExpandToggleTarget || !this.hasBodyTarget) return;

      const bodyHidden = this.bodyTarget.classList.contains("hidden");
      const overflowing = this.bodyTarget.scrollHeight > this.bodyTarget.clientHeight + 1;
      this.expandToggleTarget.classList.toggle("hidden", bodyHidden || !overflowing);
    });
  }
}
